<template>
  <div>
    <div class="dashboard" v-loading="loading">
      <grid-layout
        class="dashboard-grid-layout"
        :class="{'edit-mode': editMode}"
        :layout="userWidgets"
        :col-num="colNum"
        :row-height="30"
        :is-draggable="editMode && $canAndRulesPass(dashboard, $permissions.HOME_DASHBOARD.WIDGET_MANAGE)"
        :is-resizable="editMode && $canAndRulesPass(dashboard, $permissions.HOME_DASHBOARD.WIDGET_MANAGE)"
        :is-bounded="false"
        :vertical-compact="false"
        :use-css-transforms="true"
        :prevent-collision="true"
        :margin="[10,10]"
      >
        <grid-item
          v-for="widget in userWidgets"
          class="dashboard-grid-item"
          :class="{
            'dashboard-grid-item-hovered': (currentHover===widget.i || currentDeleting===widget.i)
          }"
          :key="widget.i"
          :x="widget.x"
          :y="widget.y"
          :w="widget.w"
          :h="widget.h"
          :i="widget.i"
          :min-w="1"
          :min-h="1"
          @resized="widgetResized"
          @moved="widgetMoved"
        >
          <div
            v-if="!widget.settings.disabled || editMode"
            class="widget-container"
            :style="{
              'box-shadow': '0 0 '+widget.settings.shadowSize+'px '+ (widget.settings.shadowColor || projectVariables.colorPrimaryLight7),
              'border': widget.settings.borderSize+'px solid '+ (widget.settings.borderColor || projectVariables.colorPrimaryLight5),
            }"
            @mouseover="widgetHovered(widget.i)"
            @mouseout="widgetUnhovered"
          >
            <div v-if="widget.settings.disabled" class="widget-container-overlay">
              <div class="widget-container-overlay-label">
                <i class="fas fa-eye-slash"></i>
                Виджет скрыт
              </div>
            </div>

            <div
              v-show="editMode && $canAndRulesPass(dashboard, $permissions.HOME_DASHBOARD.WIDGET_MANAGE) && (currentHover===widget.i || currentDeleting===widget.i)"
              :style="{
                top: widget.y===0 ? 'unset' : '-40px',
                bottom: widget.y===0 ? '-44px' : 'unset',
                right: widget.x > colNum-8 ? '0' : 'unset',
              }"
              class="widget-actions-container"
            >
              <el-tooltip
                content="Настройки"
                placement="top"
                :open-delay="500"
              >
                <el-button
                  class="widget-actions-button"
                  size="mini"
                  icon="fas fa-cog"
                  @click="openSettings(widget)"
                ></el-button>
              </el-tooltip>

              <el-tooltip
                content="Клонировать"
                placement="top"
                :open-delay="500"
              >
                <el-button
                  class="widget-actions-button"
                  size="mini"
                  icon="fas fa-copy"
                  @click="cloneWidget(widget)"
                >
                </el-button>
              </el-tooltip>

              <el-popover
                placement="bottom"
                width="250"
                trigger="click"
              >
                <div>
                  <div style="margin-bottom: 10px">Выберите дашборд для перемещения:</div>
                  <div
                    v-for="dashboardToShow in dashboards"
                    :key="dashboardToShow.id"
                    style="margin-bottom: 5px"
                  >
                    <el-button
                      :disabled="dashboardToShow.id===dashboard.id"
                      style="width: 100%; text-wrap: auto"
                      size="mini"
                      @click="moveWidgetToDashboardId(widget, dashboardToShow.id)"
                    >
                      {{ dashboardToShow.id===dashboard.id ? 'Текущий' : dashboardToShow.settings.name}}
                    </el-button>
                  </div>
                </div>

                <el-tooltip
                  slot="reference"
                  content="Переместить на дашборд"
                  placement="top"
                  :open-delay="500"
                >
                  <el-button
                    class="widget-actions-button"
                    size="mini"
                    icon="fas fa-share-square"
                  >
                  </el-button>
                </el-tooltip>
              </el-popover>

              <el-popconfirm
                confirm-button-text='Да'
                cancel-button-text='нет'
                icon="el-icon-question"
                title="Вы уверены, что хотите удалить виджет?"
                @confirm="removeWidget(widget.i)"
              >
                <el-tooltip
                  slot="reference"
                  content="Удалить"
                  placement="top"
                  :open-delay="500"
                >
                  <el-button
                    class="widget-actions-button"
                    type="danger"
                    size="mini"
                    icon="fas fa-trash"
                    @click="currentDeleting=widget.i"
                  ></el-button>
                </el-tooltip>
              </el-popconfirm>

            </div>




            <div
              v-if="widget.settings.name"
              class="widget-header"
              :style="{
                'color': widget.settings.headerTextColor || projectVariables.colorPrimary,
                'font-size': (widget.settings.headerTextSize || '16') + 'px',
                'text-align': widget.settings.headerTextAlign || 'center',
                'padding': widget.settings.headerPadding+'px',
                'background-color': widget.settings.headerBackgroundColor || projectVariables.colorPrimaryLight9,
                'border-bottom': widget.settings.headerBottomBorderSize+'px solid '+ (widget.settings.headerBottomBorderColor || projectVariables.colorPrimaryLight5),
              }"
            >
              {{widget.settings.name}}
            </div>

            <div class="widget-body">
              <component
                :is="widget.viewTemplate"
                :key="widget.renderKey"
                :ref="'widget_'+widget.id"
                :edit-mode="editMode"
                :widget="widget"
                :settings="widget.settings"
                :data="widget.data"
              >
              </component>
            </div>



          </div>
        </grid-item>
      </grid-layout>

      <div
        v-if="!loading && userWidgets.length === 0"
        class="dashboard-empty-state"
      >
        <div class="dashboard-empty-state-icon">
          <i class="fas fa-th-large"></i>
        </div>
        <div class="dashboard-empty-state-text">На этом дашборде пока нет виджетов</div>
        <el-button
          v-if="$canAndRulesPass(dashboard, $permissions.HOME_DASHBOARD.WIDGET_MANAGE)"
          type="primary"
          size="small"
          icon="fas fa-plus"
          @click="openWidgetPicker"
        >
          Добавить виджет
        </el-button>
      </div>
    </div>

    <WidgetSettingsDialog
      ref="widgetSettingsModal"
      @saved="widgetSaved"
    />

    <WidgetPickerDialog
      ref="widgetPickerDialog"
      @select="addWidget"
    />
  </div>
</template>

<script>
import VueGridLayout from 'vue-grid-layout';
import UserWidget from "@/components/home/dashboard/widgets/UserWidgetClass.js";
import WidgetSettingsDialog from "@/components/home/dashboard/widgetSettings/WidgetSettingsDialog.vue";
import WidgetPickerDialog from "@/components/home/dashboard/widgetPicker/WidgetPickerDialog.vue";
import projectVariables from "@/assets/css/element-variables.scss";


export default {
  name: 'HomepageDashboard',
  components: {
    WidgetSettingsDialog,
    WidgetPickerDialog,
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
  },
  props:{
    dashboard: {type: Object, required: true},
    dashboards: {type: Array, required: true},
    editMode: {type: Boolean, default: false},
  },
  data() {
    return {
      loading: false,
      colNum: 48,

      hoverTimeout: null,
      currentHover: null,
      currentDeleting: null,

      userWidgets: [],
      projectVariables,
    };
  },
  mounted() {
    this.loadWidgets();
  },
  methods: {
    loadWidgets(){
      this.loading = true;
      this.userWidgets = [];
      this.$api.homeDashboard.getDashboardWidgets({
        dashboard_id: this.dashboard.id,
      })
        .then((data) => {
          data.widgets.forEach(async widget => {
            const newWidget =  new UserWidget(widget);
            await newWidget.loadViewTemplate();
            this.userWidgets.push(newWidget);
          })
        })
        .finally(()=>{
          this.loading = false;
        })
    },

    async addWidget(widgetType) {
      const widgetConfig = this.$companyConfiguration.home.getWidgetConfig(widgetType);

      const newWidget = new UserWidget();
      newWidget.dashboard_id = this.dashboard.id;
      newWidget.type = widgetType;
      newWidget.x = 0;
      newWidget.y = this.getLowestPosition();

      newWidget.save()
        .then(async data => {
          const newWidget = new UserWidget(data.widget);
          await newWidget.loadViewTemplate();
          this.userWidgets.push(newWidget);
          window.scrollTo(0, document.body.scrollHeight);
          this.openSettings(newWidget);
        })
    },

    cloneWidget(widget){
      const clonedWidget = new UserWidget(widget);
      clonedWidget.setId(null);
      clonedWidget.x = 0;
      clonedWidget.y = this.getLowestPosition();
      clonedWidget.save()
        .then(async data => {
          clonedWidget.setId(data.widget.id);
          await clonedWidget.loadViewTemplate();
          this.userWidgets.push(clonedWidget);
        })
    },

    moveWidgetToDashboardId(widget, dashboardId){
      widget.dashboard_id = dashboardId;
      widget.save()
        .then(data => {
          this.userWidgets = this.userWidgets.filter(w => w.id !== widget.id);
        })
    },

    widgetMoved(id, newX, newY){
      const changedWidget = this.userWidgets.find(w => w.id === id);
      if( !changedWidget ){
        return;
      }
      changedWidget.save();
    },
    widgetResized(id, newH, newW, newHPx, newWPx){
      const changedWidget = this.userWidgets.find(w => w.id === id);
      if( !changedWidget ){
        return;
      }
      changedWidget.save();

      this.$refs['widget_'+id][0].eventWidgetResized();
    },

    widgetSaved(widget) {
      widget.renderKey++;
    },

    removeWidget(id) {
      this.$api.homeDashboard.deleteUserWidget({
        id: id,
      })
        .then(data => {
          this.userWidgets = this.userWidgets.filter(w => w.id !== id);
        })
        .catch(() => {
          this.$notify.error({
            title: 'Ошибка',
            message: 'Не смог удалить виджет'
          });
        })

    },


    openSettings(widget) {
      this.$refs.widgetSettingsModal.open(widget);
    },

    openWidgetPicker() {
      if( !this.$canAndRulesPass(this.dashboard, this.$permissions.HOME_DASHBOARD.WIDGET_MANAGE) ){
        return;
      }
      this.$refs.widgetPickerDialog.open();
    },

    getLowestPosition() {
      if (this.userWidgets.length === 0) {
        return 0;
      }
      return Math.max(...this.userWidgets.map(item => item.y + item.h));
    },

    widgetHovered(id){
      clearTimeout(this.hoverTimeout);
      this.currentHover = id;
      this.currentDeleting = this.currentDeleting===id ? id : null;
    },
    widgetUnhovered(){
      this.hoverTimeout = setTimeout(()=>{
        this.currentHover = null;
      }, 300)
    }

  }
};
</script>

<style>
.vue-grid-item.vue-draggable-dragging {
  background-color: rgba(0, 0, 0, 0.1);
}
.vue-grid-item.vue-grid-placeholder {
  background: rgba(0, 0, 0, 0.5);
  border: 2px dashed #aaa;
}
</style>

<style lang="scss">
@import "@/assets/css/element-variables";
@import "@/components/home/dashboard/_dashboard-design";

.dashboard{
  position: relative;

  &-grid-layout {
    width: 100%;
    position: relative;
    min-height: 340px;
  }

  &-grid-item.dashboard-grid-item-hovered{
    z-index: 10;
  }

  // Сетка
  &-grid-layout.edit-mode::before {
    content: '';
    background-size: calc(calc(100% - 5px) / 48) calc(30px + 10px);
    background-image:
      linear-gradient(
        to right,
        #eceef1 1px,
        transparent 1px
      ),
      linear-gradient(to bottom, #eceef1 1px, transparent 1px);
    height: calc(100% - 5px);
    width: calc(100% - 5px);
    position: absolute;
    background-repeat: repeat;
    margin:5px;
  }

  &-empty-state {
    background-color: #fff;
    border: 1px solid $--color-primary-light-7;
    border-radius: $--border-radius-base;
    position: fixed;
    top: 30%;
    left: calc(50% - 150px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 60px 20px;
    color: $dashboard-text-muted;

    &-icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: mix($dashboard-surface, $--color-primary, 92%);
      color: $--color-primary;
      font-size: 22px;
      margin-bottom: $dashboard-space-md;
    }

    &-text {
      margin-bottom: $dashboard-space-md;
      font-size: 14px;
    }
  }

  .widget-container {
    @include dashboard-surface-card;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: box-shadow .15s ease, border-color .15s ease;

    &-overlay{
      position: absolute;
      z-index: 20;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(2px);
      display: flex;
      align-items: center;
      justify-content: center;

      &-label {
        @include dashboard-empty-state-pill;
        font-size: 13px;

        i {
          margin-right: 6px;
        }
      }
    }

    .widget-actions-container {
      position: fixed;
      z-index: 3;
      display: flex;
      align-items: center;
      gap: $dashboard-space-xs;
      padding: $dashboard-space-xs;
      background-color: $dashboard-surface;
      border: 1px solid $dashboard-border-color;
      border-radius: $--border-radius-base;
      box-shadow: $dashboard-elevation-toolbar;

      .el-button + .el-button {
        margin-left: 0;
      }
    }

    .widget-body{
      width: 100%;
      height: 100%;
      flex: 1;
      min-height: 0;
    }

  }

}


</style>`